import { useState } from 'react'
import Link from '../components/LocalizedLink'
import { motion } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { Mail, Phone, MapPin, Send, Clock, MessageCircle, User, Building } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'
import SEO from '../components/SEO'
import Turnstile from '../components/Turnstile'

const translations = {
  el: {
    hero: {
      title: 'Επικοινωνήστε Μαζί Μας',
      subtitle: 'Έχετε ερωτήσεις; Θα χαρούμε να ακούσουμε. Στείλτε μας μήνυμα και θα απαντήσουμε το συντομότερο δυνατό.',
    },
    info: {
      title: 'Στοιχεία Επικοινωνίας',
      subtitle: 'Επικοινωνήστε μαζί μας μέσω οποιουδήποτε από τα παρακάτω κανάλια. Είμαστε εδώ για να βοηθήσουμε!',
      items: [
        { title: 'Email', content: 'contact@automechlabs.com', link: 'mailto:contact@automechlabs.com' },
        { title: 'Τηλέφωνο', content: '+30 695 195 6643', link: 'tel:+306951956643' },
        { title: 'Τοποθεσία', content: 'Ελλάδα', link: null },
        { title: 'Ώρες Λειτουργίας', content: 'Δευ-Παρ: 9π.μ.-5μ.μ. (EET)', link: null },
      ],
      quickNote: 'Εγγυημένη Γρήγορη Απάντηση',
      quickNoteText: 'Συνήθως απαντάμε σε όλα τα αιτήματα εντός 24 ωρών κατά τις εργάσιμες ημέρες.',
    },
    form: {
      title: 'Στείλτε Μας Μήνυμα',
      name: 'Ονοματεπώνυμο *',
      namePlaceholder: 'Ιωάννης Παπαδόπουλος',
      nameRequired: 'Το όνομα είναι υποχρεωτικό',
      nameMin: 'Το όνομα πρέπει να έχει τουλάχιστον 2 χαρακτήρες',
      email: 'Διεύθυνση Email *',
      emailPlaceholder: 'ioannis@example.com',
      emailRequired: 'Το email είναι υποχρεωτικό',
      emailInvalid: 'Μη έγκυρη διεύθυνση email',
      phone: 'Αριθμός Τηλεφώνου',
      phonePlaceholder: '+30 123 456 7890',
      company: 'Επωνυμία Εταιρείας',
      companyPlaceholder: 'Η Εταιρεία Σας',
      subject: 'Θέμα *',
      subjectPlaceholder: 'Πώς μπορούμε να σας βοηθήσουμε;',
      subjectRequired: 'Το θέμα είναι υποχρεωτικό',
      subjectMin: 'Το θέμα πρέπει να έχει τουλάχιστον 3 χαρακτήρες',
      message: 'Μήνυμα *',
      messagePlaceholder: 'Πείτε μας περισσότερα για το αίτημά σας...',
      messageRequired: 'Το μήνυμα είναι υποχρεωτικό',
      messageMin: 'Το μήνυμα πρέπει να έχει τουλάχιστον 10 χαρακτήρες',
      sending: 'Αποστολή...',
      send: 'Αποστολή Μηνύματος',
      privacy: 'Με την υποβολή αυτής της φόρμας, συμφωνείτε με την',
      privacyLink: 'Πολιτική Απορρήτου',
      successMsg: 'Το μήνυμα στάλθηκε επιτυχώς! Θα επικοινωνήσουμε σύντομα.',
      errorMsg: 'Αποτυχία αποστολής. Παρακαλώ στείλτε email απευθείας στο contact@automechlabs.com',
      captchaError: 'Παρακαλώ ολοκληρώστε την επαλήθευση ασφαλείας.',
    },
    faq: {
      title: 'Πριν Επικοινωνήσετε',
      subtitle: 'Ελέγξτε αν η ερώτησή σας απαντάται εδώ',
      items: [
        { q: 'Πόσο χρόνο χρειάζεται για να λάβω απάντηση;', a: 'Συνήθως απαντάμε εντός 24 ωρών κατά τις εργάσιμες ημέρες.' },
        { q: 'Μπορώ να ζητήσω επίδειξη;', a: 'Ναι! Αναφέρετέ το στο μήνυμά σας και θα κανονίσουμε μια κατάλληλη ώρα.' },
        { q: 'Προσφέρετε τεχνική υποστήριξη;', a: 'Ναι, όλοι οι πελάτες λαμβάνουν υποστήριξη email και τηλεφώνου.' },
        { q: 'Μπορώ να ζητήσω προσαρμοσμένες λειτουργίες;', a: 'Απολύτως! Επικοινωνήστε μαζί μας για να συζητήσουμε επιλογές προσαρμοσμένης ανάπτυξης.' },
      ],
    },
  },
  en: {
    hero: {
      title: 'Get In Touch',
      subtitle: "Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
    },
    info: {
      title: 'Contact Information',
      subtitle: "Reach out to us through any of these channels. We're here to help!",
      items: [
        { title: 'Email', content: 'contact@automechlabs.com', link: 'mailto:contact@automechlabs.com' },
        { title: 'Phone', content: '+30 695 195 6643', link: 'tel:+306951956643' },
        { title: 'Location', content: 'Greece', link: null },
        { title: 'Business Hours', content: 'Mon-Fri: 9AM-5PM (EET)', link: null },
      ],
      quickNote: 'Quick Response Guaranteed',
      quickNoteText: 'We typically respond to all inquiries within 24 hours during business days.',
    },
    form: {
      title: 'Send Us a Message',
      name: 'Full Name *',
      namePlaceholder: 'John Doe',
      nameRequired: 'Name is required',
      nameMin: 'Name must be at least 2 characters',
      email: 'Email Address *',
      emailPlaceholder: 'john@example.com',
      emailRequired: 'Email is required',
      emailInvalid: 'Invalid email address',
      phone: 'Phone Number',
      phonePlaceholder: '+30 123 456 7890',
      company: 'Company Name',
      companyPlaceholder: 'Your Company',
      subject: 'Subject *',
      subjectPlaceholder: 'How can we help you?',
      subjectRequired: 'Subject is required',
      subjectMin: 'Subject must be at least 3 characters',
      message: 'Message *',
      messagePlaceholder: 'Tell us more about your inquiry...',
      messageRequired: 'Message is required',
      messageMin: 'Message must be at least 10 characters',
      sending: 'Sending...',
      send: 'Send Message',
      privacy: 'By submitting this form, you agree to our',
      privacyLink: 'Privacy Policy',
      successMsg: "Message sent successfully! We'll get back to you soon.",
      errorMsg: 'Failed to send message. Please try emailing us directly at contact@automechlabs.com',
      captchaError: 'Please complete the security verification.',
    },
    faq: {
      title: 'Before You Contact Us',
      subtitle: 'Check if your question is answered here',
      items: [
        { q: 'How long does it take to get a response?', a: 'We typically respond within 24 hours during business days.' },
        { q: 'Can I schedule a demo?', a: "Yes! Mention it in your message and we'll arrange a convenient time." },
        { q: 'Do you offer technical support?', a: 'Yes, all customers receive priority email and phone support.' },
        { q: 'Can I request custom features?', a: 'Absolutely! Contact us to discuss custom development options.' },
      ],
    },
  },
}

const infoIcons = [
  <Mail className="h-6 w-6" />,
  <Phone className="h-6 w-6" />,
  <MapPin className="h-6 w-6" />,
  <Clock className="h-6 w-6" />,
]

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState(null)
  const { lang } = useLanguage()
  const t = translations[lang]
  const { register, handleSubmit, formState: { errors }, reset } = useForm()

  const onSubmit = async (data) => {
    if (!turnstileToken) { toast.error(t.form.captchaError); return }

    setIsSubmitting(true)
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, turnstileToken }),
      })
      if (response.ok) {
        toast.success(t.form.successMsg)
        reset()
      } else {
        throw new Error('Failed')
      }
    } catch {
      toast.error(t.form.errorMsg)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="overflow-hidden">
      <SEO
        title="Contact Us"
        description="Get in touch with the Workshop Assistant team for sales, support, or custom feature requests. We typically respond within 24 hours."
        path="/contact"
      />
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="inline-flex items-center justify-center w-24 h-24 bg-white bg-opacity-20 rounded-full mb-8">
              <MessageCircle className="h-12 w-12" />
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-xl opacity-90">{t.hero.subtitle}</motion.p>
          </div>
        </div>
      </section>

      <section className="section-container bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">{t.faq.title}</h2>
          <p className="text-xl text-gray-600 text-center mb-12">{t.faq.subtitle}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.faq.items.map((item, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="card">
                <h3 className="font-semibold text-lg mb-2">{item.q}</h3>
                <p className="text-gray-600">{item.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-container bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1 space-y-8">
            <div>
              <h2 className="text-3xl font-bold mb-6">{t.info.title}</h2>
              <p className="text-gray-600 mb-8">{t.info.subtitle}</p>
            </div>
            <div className="space-y-6">
              {t.info.items.map((info, index) => (
                <motion.div key={index} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-primary-100 text-primary-600 rounded-lg flex items-center justify-center">{infoIcons[index]}</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{info.title}</h3>
                    {info.link ? <a href={info.link} className="text-gray-600 hover:text-primary-600 transition-colors">{info.content}</a> : <p className="text-gray-600">{info.content}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="bg-primary-50 rounded-lg p-6">
              <h3 className="font-semibold text-primary-900 mb-2">{t.info.quickNote}</h3>
              <p className="text-sm text-primary-700">{t.info.quickNoteText}</p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-3xl font-bold mb-8">{t.form.title}</h2>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.form.name}</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input type="text" {...register('name', { required: t.form.nameRequired, minLength: { value: 2, message: t.form.nameMin } })} className="input-field pl-10" placeholder={t.form.namePlaceholder} />
                    </div>
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.form.email}</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input type="email" {...register('email', { required: t.form.emailRequired, pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: t.form.emailInvalid } })} className="input-field pl-10" placeholder={t.form.emailPlaceholder} />
                    </div>
                    {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.form.phone}</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input type="tel" {...register('phone')} className="input-field pl-10" placeholder={t.form.phonePlaceholder} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t.form.company}</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input type="text" {...register('company')} className="input-field pl-10" placeholder={t.form.companyPlaceholder} />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.form.subject}</label>
                  <input type="text" {...register('subject', { required: t.form.subjectRequired, minLength: { value: 3, message: t.form.subjectMin } })} className="input-field" placeholder={t.form.subjectPlaceholder} />
                  {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject.message}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t.form.message}</label>
                  <textarea rows="6" {...register('message', { required: t.form.messageRequired, minLength: { value: 10, message: t.form.messageMin } })} className="input-field resize-none" placeholder={t.form.messagePlaceholder} />
                  {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>}
                </div>
                <div className="flex justify-center">
                  <Turnstile onVerify={setTurnstileToken} />
                </div>
                <button type="submit" disabled={isSubmitting || !turnstileToken} className="w-full btn-primary py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2">
                  {isSubmitting ? <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /><span>{t.form.sending}</span></> : <><Send className="h-5 w-5" /><span>{t.form.send}</span></>}
                </button>
                <p className="text-sm text-gray-500 text-center">{t.form.privacy}{' '}<Link to="/privacy" className="text-primary-600 hover:underline">{t.form.privacyLink}</Link></p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Contact
